import React, { useMemo, useState, useEffect, Suspense, lazy } from 'react';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import FormField from '../../../atoms/Form/Field/FormField';
import FieldError from '../../Error/FieldError';
import Input from '../../../atoms/Input/Input';
import AdvancedSelect from '../../../atoms/AdvancedSelect/AdvancedSelect';
import { countries } from '../../../../constants/countries';
import { components } from 'react-select';

const Flag = lazy(() => import('../../../atoms/Flag/Flag'));
import styles from './AddressField.css';
import useTranslate from '../../../../hooks/useTranslate';
import { getConfidenceFieldStyle } from '../../../../helpers/form';
import Select from '../../../atoms/Select/Select';
// Custom advanced-select components to display country flag
const Option = ({ data, ...props }) => (
  <components.Option {...props}>
    <Suspense>
      <Flag className={styles.AddressField__optionFlag} countryCode={data.value} />
    </Suspense> {data.label}
  </components.Option>
);

Option.propTypes = {
  data: PropTypes.object,
};

const SingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props}>
    <Suspense>
      <Flag className={styles.AddressField__optionFlag} countryCode={data.value} />
    </Suspense> {data.label}
  </components.SingleValue>
);

SingleValue.propTypes = {
  data: PropTypes.object,
};

const AddressRender = ({
  input,
  label,
  placeholder,
  id,
  validation: { onlyCountry, defaultFrance } = {},
  readonly,
  confidence,
  isFocusOnInit,
  meta: { error, touched },
}) => {
  const Component = useMemo(() => (onlyCountry ? AdvancedSelect : Input), [onlyCountry]);
  const i18n = useTranslate();
  const placeholderMemo = useMemo(() => {
    return placeholder || placeholder === ''
      ? placeholder
      : i18n('AdvancedSelectField.placeholder');
  }, [placeholder]);

  const style = useMemo(() => {
    if (touched) return null;
    return getConfidenceFieldStyle(confidence);
  }, [confidence, touched]);

  ////////// Google autocomplete adress api
  const [predictions, setPredictions] = useState([]);
  const [pretictedValue, setPredictedValue] = useState('');

  const [, setValues] = useState('');

  const googleAutocomplete = async (text) =>
    new Promise((resolve, reject) => {
      try {
        new window.google.maps.places.AutocompleteService().getPlacePredictions(
          { input: text },
          resolve,
        );
      } catch (e) {
        reject(e);
      }
    });

  const debounceTimeout = 400;

  useEffect(() => {
    if (!onlyCountry) {
      const handleDebounce = setTimeout(async () => {
        try {
          if (!input.value) {
            return;
          }

          const nextPredictions = await googleAutocomplete(input.value);
          let newKeyPredictionsArray = nextPredictions
            ? nextPredictions.map((item) => {
              return {
                value: item.description,
                label: item.description,
              };
            })
            : [];
          setPredictions(newKeyPredictionsArray);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }, debounceTimeout);

      return () => {
        clearTimeout(handleDebounce);
      };
    }
  }, [input.value, debounceTimeout, pretictedValue]);

  const streetSuggestions = () =>
    pretictedValue !== input.value && (
      <Select
        options={predictions}
        onChange={onClickSuggestions}
        className={styles.selectStreet}
        size={3}
      />
    );

  const onClickSuggestions = (values) => {
    input['value'] = values;
    setValues(values);
    setPredictedValue(values);
    document.querySelector(`[name$=${input.name}]`).focus();
  };
  return (
    <>
      <Component
        isFocusOnInit={isFocusOnInit}
        {...input}
        error={!!(touched && error)}
        value={input.value}
        onFocus={() => {
          return null;
        }}
        label={label}
        id={id}
        block
        placeholder={(onlyCountry && placeholderMemo) || placeholder}
        readonly={readonly}
        {...(onlyCountry && {
          options: countries(),
          autoselect: false,
          components: { Option, SingleValue },
          defaultFrance,
        })}
        style={style}
        className={
          predictions.length > 0 &&
            pretictedValue !== input.value &&
            input.value !== ''
            ? styles.googleApiActive
            : undefined
        }
      />
      {touched && error && <FieldError error={error} />}
      {!readonly &&
        !onlyCountry &&
        predictions &&
        predictions.length > 0 &&
        pretictedValue !== input.value &&
        input.value !== '' &&
        streetSuggestions()}
    </>
  );
};

AddressRender.propTypes = {
  isFocusOnInit: PropTypes.bool,
  input: PropTypes.object,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  meta: PropTypes.shape({
    error: PropTypes.any,
    touched: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    onlyCountry: PropTypes.bool,
    defaultFrance: PropTypes.bool,
  }),
  id: PropTypes.string,
  readonly: PropTypes.bool,
  confidence: PropTypes.number,
};

const AddressField = ({
  name,
  label,
  required,
  placeholder,
  validation = {},
  fieldHoC: FieldHOC = Field,
  answersChoices = null,
  confidence,
  ...props
}) => {
  const id = useMemo(() => uuid(), [name]);

  return (
    <FormField
      label={label}
      name={name}
      htmlFor={id}
      required={required}
      verticalAlign={true}
      answersChoices={answersChoices}
      confidence={confidence}
    >
      <FieldHOC
        name={name}
        placeholder={placeholder}
        component={AddressRender}
        id={id}
        validation={validation}
        confidence={confidence}
        {...props}
      />
    </FormField>
  );
};

AddressField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  validation: PropTypes.object,
  fieldHoC: PropTypes.func,
  answersChoices: PropTypes.array,
  confidence: PropTypes.number,
};

export default AddressField;
